/**
 * History API 기반 SPA 라우터
 * - `/` → home
 * - `/school`, `/school/snack`, `/about` 등 path로 페이지 구분
 * - `#service` 같은 페이지 내 앵커는 그대로 브라우저 기본 동작
 * - Vite `base`(서브경로 등) 하위 배포를 지원
 */
import { BASE_URL, stripBase, withBase } from './base';
import { applyPageSeo, type SeoPageId } from './seo';

export type PageId =
  | 'home'
  | 'archive'
  | 'privacy'
  | 'terms'
  | 'notice'
  | 'about'
  | 'contact'
  | 'faq'
  | 'school'
  | 'schoolSnack'
  | 'season'
  | 'care'
  | 'notFound';

export type RouteMatch = {
  page: PageId;
  params: Record<string, string>;
};

type RouteHandler = (match: RouteMatch) => void | Promise<void>;

/** pathname → 라우트 (base path 제거 후 매칭) */
export function parsePath(pathname = window.location.pathname): RouteMatch {
  const parts = stripBase(pathname).replace(/\/+$/, '').split('/').filter(Boolean);
  const [segment, id] = parts;
  const queryId = new URLSearchParams(window.location.search).get('id') ?? '';

  if (!segment) {
    return { page: 'home', params: {} };
  }

  // /index.html → 홈
  if (segment === 'index.html') {
    return { page: 'home', params: {} };
  }

  if (segment === '404' || segment === '404.html') {
    return { page: 'notFound', params: {} };
  }

  switch (segment) {
    case 'board':
    case 'archive':
      return { page: 'archive', params: { id: id || queryId } };
    case 'privacy':
      return { page: 'privacy', params: {} };
    case 'terms':
      return { page: 'terms', params: {} };
    case 'notice':
      return { page: 'archive', params: { id: id || queryId } };
    case 'about':
      return { page: 'about', params: {} };
    case 'contact':
      return { page: 'contact', params: {} };
    case 'faq':
      return { page: 'faq', params: { id: id || queryId } };
    case 'school':
      if (id === 'snack') {
        return { page: 'schoolSnack', params: {} };
      }
      return { page: 'school', params: {} };
    case 'season':
      return { page: 'season', params: {} };
    case 'care':
      return { page: 'care', params: {} };
    default:
      return { page: 'notFound', params: {} };
  }
}

/** 예전 `#/path` 북마크를 base 하위 `/path`로 한 번 보정 */
function migrateLegacyHash() {
  const hash = window.location.hash;
  if (!hash.startsWith('#/')) return;

  const path = withBase(`/${hash.slice(2)}`.replace(/\/+/g, '/'));
  history.replaceState(null, '', path || BASE_URL);
}

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function shouldInterceptLink(anchor: HTMLAnchorElement, event: MouseEvent) {
  if (isModifiedClick(event)) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) return false;

  // 페이지 내 앵커만 있는 경우 (#service, #main-content)
  if (href.startsWith('#')) return false;

  let url: URL;
  try {
    url = new URL(anchor.href, window.location.origin);
  } catch {
    return false;
  }

  if (url.origin !== window.location.origin) return false;

  // 같은 path + 해시만 다른 경우 → 네이티브 스크롤
  if (url.pathname === window.location.pathname && url.hash) return false;

  // base 밖의 경로는 인터셉트하지 않음
  const base = BASE_URL.replace(/\/+$/, '') || '';
  if (base && url.pathname !== base && !url.pathname.startsWith(`${base}/`)) {
    return false;
  }

  return true;
}

export function navigate(to: string, options?: { replace?: boolean }) {
  const url = new URL(to, window.location.origin);
  // 앱 내부 경로면 base 보장
  const appPath = stripBase(url.pathname);
  const nextPath = withBase(appPath === '/' ? '/' : appPath);
  const next = `${nextPath}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (next === current) {
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  if (options?.replace) {
    history.replaceState(null, '', next);
  } else {
    history.pushState(null, '', next);
  }

  window.dispatchEvent(new PopStateEvent('popstate'));
}

function toSeoPage(page: PageId): SeoPageId {
  if (page === 'notice') return 'archive';
  return page;
}

export function createRouter(onRoute: RouteHandler): () => void {
  migrateLegacyHash();

  const handle = () => {
    const match = parsePath();
    applyPageSeo(toSeoPage(match.page));
    void onRoute(match);
  };

  const onClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a');
    if (!anchor || !shouldInterceptLink(anchor, event)) return;

    event.preventDefault();
    const url = new URL(anchor.href, window.location.origin);
    navigate(`${url.pathname}${url.search}${url.hash}`);
  };

  window.addEventListener('popstate', handle);
  document.addEventListener('click', onClick);
  handle();

  return () => {
    window.removeEventListener('popstate', handle);
    document.removeEventListener('click', onClick);
  };
}
